import {calculateDaysDiff} from '@/lib/utils';
import {flattenTree} from './tree';
import {FlowEdge, FlowNode, Tree, TreeNode} from './types';
import {connect_orphaned_nodes} from './connect_orphan_nodes';
import {defaultEdge} from '../const';

export type AutoCalculateDatesResult = {
  newNodes: FlowNode[];
  error?: string | null;
};

export function auto_calculate_dates(
  start_node: FlowNode,
  nodes: FlowNode[],
  edges: FlowEdge[],
  max_work_days?: number,
): AutoCalculateDatesResult {
  const project_start_date = start_node.data?.start_date as Date;
  if (!project_start_date) return {newNodes: nodes};
  // Create a map of node IDs to their calculated dates
  const nodeDates = new Map();
  nodeDates.set(start_node.id, {
    //
    start_date: new Date(project_start_date),
    initial_end_date: new Date(project_start_date),
    delay_days: 0,
    //
    rescheduled_work_days: 0,
    rescheduled_start_date: new Date(project_start_date),
    rescheduled_end_date: new Date(project_start_date),
    rescheduled_delay_days: 0,
    //
    actual_work_days: 0,
    actual_start_date: new Date(project_start_date),
    actual_end_date: new Date(project_start_date),
  });

  // Helper to get source nodes for a target node
  const getSourceNodes = targetId => {
    return edges
      .filter(edge => edge.target === targetId)
      .map(edge => nodes.find(node => node.id === edge.source))
      .filter(Boolean);
  };

  // Process nodes in topological order
  const processedNodes = new Set([start_node.id]);
  let changed = true;

  while (changed) {
    changed = false;
    nodes.forEach(node => {
      if (processedNodes.has(node.id)) return;

      // check if all source nodes are processed
      const sourceNodes = getSourceNodes(node.id);

      if (sourceNodes.every(src => src && processedNodes.has(src.id))) {
        ////// Tính toán theo kế hoạch
        // calculate initial end date as max of source nodes' initial end dates + expected work days
        const source_initial_end_date = new Date(
          Math.max(...sourceNodes.map(n => n && nodeDates.get(n.id).initial_end_date.getTime())),
        );
        const start_date = new Date(
          source_initial_end_date.getTime() +
            (node.data?.start_date_offset || 0) * 24 * 60 * 60 * 1000,
        );
        const initial_end_date = new Date(
          start_date.getTime() + (node.data?.expected_work_days || 0) * 24 * 60 * 60 * 1000,
        );
        const delay_days = node.data?.actual_end_date
          ? calculateDaysDiff(initial_end_date, node.data?.actual_end_date)
          : calculateDaysDiff(initial_end_date, new Date());

        ////// Tinh toán theo kế hoạch điều chỉnh
        const rescheduled_work_days = node.data?.expected_work_days;

        // Calculate start date as max of source nodes' actual end dates or rescheduled end dates
        const sourceEndDates = sourceNodes.map(n =>
          n
            ? nodeDates.get(n.id).actual_end_date ||
              nodeDates.get(n.id).rescheduled_end_date ||
              nodeDates.get(n.id).initial_end_date ||
              project_start_date
            : project_start_date,
        );
        const maxSourceEndDate = new Date(Math.max(...sourceEndDates.map(d => d.getTime())));
        const rescheduled_start_date = new Date(
          maxSourceEndDate.getTime() +
            (node.data?.rescheduled_start_date_offset || 0) * 24 * 60 * 60 * 1000,
        );
        const rescheduled_end_date = new Date(
          rescheduled_start_date.getTime() + (rescheduled_work_days || 0) * 24 * 60 * 60 * 1000,
        );
        const rescheduled_delay_days = node.data?.actual_end_date
          ? calculateDaysDiff(rescheduled_end_date, node.data?.actual_end_date)
          : calculateDaysDiff(rescheduled_end_date, new Date());

        ////// Tính toán theo thực tế
        const actual_start_date = rescheduled_start_date;
        const actual_work_days = node.data?.actual_end_date
          ? calculateDaysDiff(actual_start_date, node.data?.actual_end_date)
          : 0;

        // Update node dates
        nodeDates.set(node.id, {
          //
          start_date,
          initial_end_date,
          delay_days,
          //
          rescheduled_work_days,
          rescheduled_start_date,
          rescheduled_end_date,
          rescheduled_delay_days,
          //
          actual_work_days,
          actual_start_date,
          actual_end_date: node.data?.actual_end_date,
        });

        processedNodes.add(node.id);
        changed = true;
      }
    });
  }

  // Update nodes with calculated dates
  const newNodes = nodes.map(node => {
    const dates = nodeDates.get(node.id);
    if (!dates) return node;
    return {
      ...node,
      data: {
        ...node.data,
        ...dates,
        // trường hợp có ngày kết thúc thực tế thì cập nhật status
        status: node.data?.actual_end_date ? 'completed' : node.data?.status,
      },
    };
  });
  console.log('Calculate dates - Updated nodes:', newNodes);

  // Check if any node's end date exceeds the maximum allowed project duration
  let error: string | null = null;
  if (max_work_days !== undefined) {
    const latest_end_date = new Date(
      Math.max(
        ...newNodes.map(node =>
          node.data?.initial_end_date
            ? node.data.initial_end_date.getTime()
            : project_start_date.getTime(),
        ),
      ),
    );

    const total_days = calculateDaysDiff(project_start_date, latest_end_date) || 0;

    if (total_days > max_work_days) {
      error = `Tổng thời gian công việc (${total_days} ngày) vượt quá thời gian tối đa cho phép của công việc trên (${max_work_days} ngày)`;
    }
  }

  return {newNodes, error};
}

export function normalize_workflow_nodes(
  _nodes: FlowNode[],
  _edges: FlowEdge[],
  max_work_days?: number
): {nodes: FlowNode[]; edges: FlowEdge[]; error?: string} {
  // Connect orphan nodes to the root
  const {nodes, edges} = connect_orphaned_nodes(_nodes, _edges, defaultEdge);
  // Calculate dates
  const start_node = nodes.find(n => n.type === 'start');
  if (!start_node) return {nodes, edges};
  const {newNodes, error} = auto_calculate_dates(start_node, nodes, edges, max_work_days);
  return {nodes: newNodes, edges, error: error || undefined};
}
